import { type FormEvent, useEffect, useState } from "react";

import { getProfessions, type PublicProfession } from "../api/publicChat";
import { reportPublicJanError } from "../utils/reportPublicJanError";

interface Props {
  onSubmit: (email: string, professionId: string) => Promise<void>;
  submitting?: boolean;
}

const isValidEmail = (s: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && s.length <= 254;

type Step = "profession" | "email";

export const EmailGateModal = ({ onSubmit, submitting }: Props) => {
  const [step, setStep] = useState<Step>("profession");
  const [professionId, setProfessionId] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [professions, setProfessions] = useState<PublicProfession[]>([]);
  const [loadingProfessions, setLoadingProfessions] = useState(true);

  // Professions come from NovoPsych's profession list (proxied by the backend),
  // not the note-template categories the dropdown previously (wrongly) used.
  useEffect(() => {
    let active = true;
    getProfessions()
      .then((list) => {
        if (active) setProfessions(list);
      })
      .catch((err) => {
        reportPublicJanError("load-professions", err);
      })
      .finally(() => {
        if (active) setLoadingProfessions(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleProfessionContinue = (e: FormEvent) => {
    e.preventDefault();
    if (!professionId) return;
    setError(null);
    setStep("email");
  };

  const handleEmailContinue = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    setError(null);
    await onSubmit(email, professionId);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-xl font-semibold text-slate-900">
          Get more relevant responses for your work
        </h2>

        {step === "profession" ? (
          <form onSubmit={handleProfessionContinue} className="space-y-4">
            <p className="text-sm text-slate-700">
              To continue chatting, please enter your profession
            </p>
            <div>
              <label
                htmlFor="public-jan-profession"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Profession
              </label>
              <select
                id="public-jan-profession"
                required
                value={professionId}
                onChange={(e) => setProfessionId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#07185A] focus:border-transparent bg-white disabled:opacity-60"
                disabled={submitting || loadingProfessions}
              >
                <option value="" disabled>
                  {loadingProfessions
                    ? "Loading professions…"
                    : "Select your profession"}
                </option>
                {professions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={submitting || !professionId}
              className="w-full py-2.5 px-4 bg-[#07185A] text-white rounded-md font-medium hover:bg-[#0a2070] transition-colors disabled:opacity-50"
            >
              Continue
            </button>
          </form>
        ) : (
          <form onSubmit={handleEmailContinue} className="space-y-4">
            <p className="text-sm text-slate-700">
              To continue chatting, enter your work email below and we'll send
              you a link to this assistant for future reference.
            </p>
            <div>
              <label
                htmlFor="public-jan-email"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Work email
              </label>
              <input
                id="public-jan-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#07185A] focus:border-transparent"
                placeholder="you@clinic.com"
                disabled={submitting}
              />
              {error ? (
                <p className="mt-1 text-xs text-red-600">{error}</p>
              ) : null}
            </div>
            <button
              type="submit"
              disabled={submitting || !email}
              className="w-full py-2.5 px-4 bg-[#07185A] text-white rounded-md font-medium hover:bg-[#0a2070] transition-colors disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
